// 1. **Dynamic Banner Rendering**: Displays banners dynamically based on the provided `bannerData`.
// 2. **Responsive Design**: Adapts content to desktop or mobile screens using `useMediaQuery`.
// 3. **Date Range Filtering**: Ensures banners are shown only if their date range is valid.
// 4. **Media Handling**: Supports images, videos, and text, rendering the appropriate media type per screen size.
// 5. **Overlay Text**: Displays overlay content, such as titles or descriptions, tailored for the screen type.
// 6. **Call-to-Action Buttons**: Includes interactive buttons with dynamic styles based on banner data.
// 7. **Utility Functions**: Uses helper functions to validate image URLs and determine date range eligibility.

import useMediaQuery from 'helpers/hooks/useMediaQuery';
import React, { useEffect } from 'react';
import { desktop, tablet, mobile } from 'helpers/utils/screensizes';
import { ContentLink, DataHomepage, Media, Overlay, OverlayContent, OverlayText } from 'types/next';
import Link from 'components/commercetools-ui/atoms/link';
import useLocale from 'helpers/hooks/useLocale';
import { usePathname } from 'next/navigation';
import { useAccount, useCart } from 'frontastic';
import { DataLayerHelpers, DataLayerOnLoad } from 'helpers/dataLayerHelpers';
interface bannerData {
  bannerData: DataHomepage;
}

const HomePageBanner = (bannerData: bannerData) => {
  const banners = bannerData?.bannerData?.data?.data?.dataSource?.contentLinks;
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const [ismobile] = useMediaQuery(mobile);

  const isImageUrl = (url: string) => {
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
  };
  const isVideoUrl = (url: string) => /\.(mp4|webm|ogg)$/i.test(url);
  const isWithinDateRange = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now <= end;
  };
  const { ctLocale: locale } = useLocale();
  const pathname = usePathname();

  const { account } = useAccount();
  const { getCountry, getCurrency } = useLocale();

  useEffect(() => {
    const localeData = {
      country: getCountry(),
      currency: getCurrency(),
    };
    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerOnLoad(currentUrl, '/', localeData, account);
    dataLayer.setDataLayerOnLoad();
  }, []);

  return (
    <div className="relative">
      {banners &&
        banners?.length > 0 &&
        banners?.map((banner: ContentLink, bannerIndex: number) => {
          const { dateRange } = banner?.filters;
          if (!isWithinDateRange(dateRange?.startDate, dateRange?.endDate)) {
            // If banner is expired means, we will show this default banner
            return (
              <>
                <Link link={'/sale'}>
                  <div className="default-banner relative flex h-[100%] w-[100%] flex-col items-center justify-center">
                    <img
                      src={`https://cdn.media.amplience.net/i/americangolf/Sale-Generic-1960x500`}
                      alt="american golf hero banner"
                      title="american golf hero banner"
                      className="relative h-[100%] w-[100%]"
                    />
                  </div>
                </Link>
              </>
            );
          }

          const filteredMedia = banner?.media?.find((media: Media) =>
            isDesktopSize
              ? media?.screentype === 'desktop'
              : ismobile
              ? media?.screentype === 'mobile'
              : media?.screentype === 'mobile',
          );
          const filterOverlayText = banner?.overlayText?.find((overlay: OverlayText) =>
            ismobile ? overlay?.screentype === 'mobile' : overlay?.screentype === 'desktop',
          );

          const mediaImage = banner?.media?.find(
            (media: Media) =>
              media.type === 'image' &&
              (isDesktopSize
                ? media?.screentype === 'desktop'
                : ismobile
                ? media?.screentype === 'mobile'
                : media?.screentype === 'mobile'),
          );

          const mediaText = banner?.media?.find(
            (media: Media) =>
              media?.type === 'text' && (ismobile ? media?.screentype === 'mobile' : media?.screentype === 'desktop'),
          );

          const mediaVideo = banner?.media?.find(
            (media: Media) =>
              media?.type === 'video' &&
              (isDesktopSize
                ? media?.screentype === 'desktop'
                : ismobile
                ? media?.screentype === 'mobile'
                : media?.screentype === 'mobile'),
          );

          const isValidVideo =
            mediaVideo?.video &&
            mediaVideo?.video?.defaultHost &&
            mediaVideo?.video?.endpoint &&
            mediaVideo?.video?.name;

          const isValidImage =
            mediaImage?.image &&
            mediaImage?.image?.defaultHost &&
            mediaImage?.image?.endpoint &&
            mediaImage?.image?.name;

          const overlayTextImage = banner?.overlayText?.find(
            (overlay: OverlayText) =>
              overlay?.type === 'image' &&
              (isDesktopSize
                ? overlay?.screentype === 'desktop' || overlay?.screentype === 'destop'
                : ismobile
                ? overlay?.screentype === 'mobile'
                : overlay?.screentype === 'mobile'),
          );

          const overlayTextVideo = banner?.overlayText?.find(
            (overlay: OverlayText) =>
              overlay?.type === 'video' &&
              (isDesktopSize
                ? overlay?.screentype === 'desktop' || overlay?.screentype === 'destop'
                : ismobile
                ? overlay?.screentype === 'mobile'
                : overlay?.screentype === 'mobile'),
          );
          const overlayTextText = banner?.overlayText?.find(
            (overlay: OverlayText) =>
              overlay?.type === 'text' &&
              (isDesktopSize
                ? overlay?.screentype === 'desktop' || overlay?.screentype === 'destop'
                : ismobile
                ? overlay?.screentype === 'mobile'
                : overlay?.screentype === 'mobile'),
          );

          return (
            <div
              key={bannerIndex}
              className="relative flex h-[100%] w-[100%] flex-col items-center justify-center"
              style={{
                backgroundColor: filteredMedia?.backgroundColour || 'transparent',
              }}
            >
              {isValidVideo ? (
                <video
                  src={`https://${mediaVideo?.video?.defaultHost}/v/${mediaVideo?.video?.endpoint}/${mediaVideo?.video?.name}/mp4_720p`}
                  loop
                  autoPlay
                  muted
                  playsInline
                  className="relative h-[100%] w-[100%] object-cover"
                />
              ) : isValidImage ? (
                <img
                  src={`https://${mediaImage?.image?.defaultHost}/i/${mediaImage?.image?.endpoint}/${mediaImage?.image?.name}`}
                  alt={mediaImage?.altText || 'Banner image'}
                  className="relative h-[100%] w-[100%]"
                />
              ) : // : mediaText ? (
              //   <h1 className="text-center text-white">{filteredMedia?.text}</h1>
              // )
              null}

              {(overlayTextImage || overlayTextVideo || overlayTextText) && (
                <div className="absolute bottom-[135px] my-4 flex flex-col items-center text-23 font-bold text-white md:bottom-75">
                  {overlayTextImage && (
                    <div className="flex w-full justify-center">
                      {overlayTextImage?.overlayContent?.map((content: OverlayContent, index: number) => (
                        <img
                          key={index}
                          src={`https://${content?.overlayImage?.defaultHost}/i/${content?.overlayImage?.endpoint}/${content?.overlayImage?.name}`}
                          alt="overlay image"
                          className="mb-2 w-[50%]"
                        />
                      ))}
                    </div>
                  )}

                  {overlayTextVideo && (
                    <div className="flex w-full justify-center">
                      {overlayTextVideo?.overlayContent?.map((content: OverlayContent, index: number) => (
                        <video
                          key={index}
                          src={`https://${content?.overlayVideo?.defaultHost}/i/${content?.overlayVideo?.endpoint}/${content?.overlayVideo?.name}`}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="mb-2 w-[50%]"
                        />
                      ))}
                    </div>
                  )}

                  {overlayTextText && (
                    <div className="flex w-full flex-col items-center justify-center text-[23px] font-bold text-white sm:bottom-[75px]">
                      {overlayTextText?.overlayContent?.map((content: OverlayContent, index: number) => (
                        <h1 key={index} style={{ color: content?.overlayTextColour }}>
                          {content?.overlayContent}
                        </h1>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="absolute bottom-15 mx-5 w-full md:bottom-10 lg:bottom-20 ">
                <div className="grid w-full grid-cols-2 gap-16 justify-self-center px-15 text-center md:w-fit md:grid-cols-4 md:gap-0 lg:flex lg:justify-center">
                  {banner &&
                    banner?.overlay?.length > 0 &&
                    banner?.overlay?.map((button: Overlay, index: number) => {
                      return (
                        <Link link={button?.cta?.link || ''} key={index}>
                          <button
                            className={`flex h-[40px] w-full items-center justify-center
      rounded-sm font-bold uppercase text-black md:mx-10 md:my-4 md:h-[33px] md:w-[170px] lg:h-[43px] lg:w-[200px]`}
                            style={{
                              backgroundColor: button?.cta?.buttonBackgroundColour,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = button?.cta?.HoverButtonBackgroundColour;
                              e.currentTarget.style.color = button?.cta?.hoverButtonTextColour;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = button?.cta?.buttonBackgroundColour;
                              e.currentTarget.style.color = 'black'; // Default text color
                            }}
                          >
                            {button?.cta?.text}
                          </button>
                        </Link>
                      );
                    })}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default HomePageBanner;
